<template>
  <div class="ko">
    <div v-for="(o, i) in shown" :key="i" class="ko-item">
      <div v-if="o.kind === 'table'" class="ko-table-scroll">
        <div v-if="o.name" class="ko-label">{{ o.name }}</div>
        <table class="ae-table">
          <thead><tr><th v-for="c in o.columns" :key="c">{{ c }}</th></tr></thead>
          <tbody>
            <tr v-for="(r, ri) in o.rows" :key="ri"><td v-for="c in o.columns" :key="c" class="ae-mono">{{ r[c] }}</td></tr>
          </tbody>
        </table>
        <div v-if="o.row_count != null" class="ko-muted">{{ Number(o.row_count).toLocaleString() }} rows<template v-if="o.truncated"> · first {{ (o.rows || []).length }} shown</template></div>
      </div>
      <div v-else-if="o.kind === 'effect'" class="ko-facts">
        <div class="ko-fact"><div class="ko-k">Effect</div><div class="ko-v">{{ o.name }}</div></div>
        <div class="ko-fact"><div class="ko-k">Estimate</div><div class="ko-v ae-mono">{{ o.estimate }}</div></div>
        <div class="ko-fact"><div class="ko-k">95% CI</div><div class="ko-v ae-mono">{{ o.ci_low }} to {{ o.ci_high }}</div></div>
        <div class="ko-fact"><div class="ko-k">p</div><div class="ko-v ae-mono">{{ o.p }}</div></div>
        <div v-if="o.model" class="ko-fact ko-wide"><div class="ko-k">Model</div><div class="ko-v">{{ o.model }}</div></div>
      </div>
      <div v-else-if="o.kind === 'frame'" class="ko-muted">
        Saved frame <router-link :to="`/impact/frames/${o.id}`" class="text-neutral-800">{{ o.name }}</router-link> · {{ Number(o.rows || 0).toLocaleString() }} rows
      </div>
      <div v-else-if="o.kind === 'input' && o.source === 'frame'" class="ko-muted">
        Read saved frame <router-link :to="`/impact/frames/${o.id}`" class="text-neutral-800">{{ o.name }}</router-link> · {{ Number(o.rows || 0).toLocaleString() }} rows · <span class="ae-mono" :title="o.sha256">sha {{ String(o.sha256 || '').slice(0, 12) }}</span>
      </div>
      <div v-else-if="o.kind === 'input'" class="ko-muted">
        Queried {{ o.engine || 'warehouse' }} · {{ Number(o.rows || 0).toLocaleString() }} rows · <span class="ae-mono" :title="`SQL ${o.sql_sha256} · rows ${o.fingerprint}`">sql {{ String(o.sql_sha256 || '').slice(0, 12) }} · rows {{ String(o.fingerprint || '').slice(0, 12) }}</span>
      </div>
      <div v-else-if="o.kind === 'image'" class="ko-image">
        <img :src="`data:image/png;base64,${o.png}`" :alt="o.text || 'plot'">
      </div>
      <HtmlFrame v-else-if="o.kind === 'html'" :html="o.html" />
      <div v-else-if="o.kind === 'markdown'" class="ko-html" v-html="sanitize(markdown(o.text))" />
      <pre v-else-if="o.kind === 'error'" class="ko-error">{{ o.text }}</pre>
      <pre v-else-if="o.kind === 'stream'" class="ko-console">{{ o.text }}</pre>
      <pre v-else class="ko-text">{{ o.text || '' }}</pre>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import HtmlFrame from './HtmlFrame.vue';

const TABLE = 'application/vnd.kairon.table+json';
const FRAME = 'application/vnd.kairon.frame+json';
const EFFECT = 'application/vnd.kairon.effect+json';
const INPUT = 'application/vnd.kairon.input+json';

function text(value) {
  return Array.isArray(value) ? value.join('') : (value || '');
}

export function flattenOutputs(outputs) {
  const out = [];
  for (const o of outputs || []) {
    const data = o.data || {};
    if (o.output_type === 'stream') out.push({ kind: 'stream', text: text(o.text) });
    else if (o.output_type === 'error') out.push({ kind: 'error', text: (o.traceback || []).join('\n').replace(/\x1b\[[0-9;]*[A-Za-z]/g, '') || `${o.ename}: ${o.evalue}` });
    else if (data[TABLE]) out.push({ kind: 'table', ...data[TABLE] });
    else if (data[FRAME]) out.push({ kind: 'frame', ...data[FRAME] });
    else if (data[EFFECT]) out.push({ kind: 'effect', ...data[EFFECT] });
    else if (data[INPUT]) out.push({ kind: 'input', ...data[INPUT], source: data[INPUT].kind });
    else if (data['image/png']) out.push({ kind: 'image', png: text(data['image/png']).replace(/\s+/g, ''), text: text(data['text/plain']) });
    else if (data['text/html']) out.push({ kind: 'html', html: text(data['text/html']) });
    else if (data['text/markdown']) out.push({ kind: 'markdown', text: text(data['text/markdown']) });
    else if (data['text/plain']) out.push({ kind: 'text', text: text(data['text/plain']) });
  }
  return out;
}

export default {
  name: 'KernelOutput',
  components: { HtmlFrame },
  props: {
    outputs: { type: Array, default: () => [] },
    hideStreams: { type: Boolean, default: false },
  },
  computed: {
    shown() {
      const all = flattenOutputs(this.outputs);
      return this.hideStreams ? all.filter((o) => o.kind !== 'stream') : all;
    },
  },
  methods: {
    sanitize(html) {
      return DOMPurify.sanitize(String(html || ''));
    },
    markdown(md) {
      return marked.parse(String(md || ''), { breaks: true, gfm: true });
    },
  },
};
</script>

<style scoped>
.ko { display: flex; flex-direction: column; gap: 8px; padding: 8px 12px; }
.ko-label { font-size: 11px; color: #56534d; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 4px; }
.ko-muted { font-size: 11px; color: #8b877f; }
.ko-table-scroll { max-height: 360px; overflow: auto; }
.ko-facts { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; border: 1px solid #e4e0d9; border-radius: 6px; padding: 8px 12px; }
.ko-fact { min-width: 0; }
.ko-wide { grid-column: span 4; }
.ko-k { font-size: 10px; color: #56534d; text-transform: uppercase; letter-spacing: 0.04em; }
.ko-v { font-size: 13px; color: #1a1918; font-weight: 500; overflow-wrap: anywhere; }
.ko-image img { max-width: 100%; display: block; }
.ko-html { font-size: 13px; overflow: auto; }
.ko-html :deep(table) { border-collapse: collapse; font-size: 12px; }
.ko-html :deep(th), .ko-html :deep(td) { padding: 3px 8px; border-bottom: 1px solid #e4e0d9; }
.ko-text, .ko-console { margin: 0; white-space: pre-wrap; font-family: 'IBM Plex Mono', monospace; font-size: 12px; line-height: 1.5; color: #38352f; max-height: 320px; overflow: auto; }
.ko-error { margin: 0; white-space: pre-wrap; font-family: 'IBM Plex Mono', monospace; font-size: 12px; color: #c23a28; max-height: 320px; overflow: auto; }
.ko-console { background: #f6f5f3; border: 1px solid #e4e0d9; border-radius: 6px; padding: 8px 10px; }
</style>
